import type { IconButtonProps } from "@bonusx-global/bx-design-system";
import { Badge, Common, IconButton, Positioning } from "@bonusx-global/bx-design-system";
import { ArrowDownOutlined } from "@bonusx-global/bx-icons";
import styled from "@emotion/styled";
import isFunction from "lodash/isFunction";
import { memo, useCallback, useContext, useEffect, useState } from "react";
import { ChatbotContext } from "../../chatbot.context";
import { SenderTypeEnum } from "../../enums/sender-type.enum";

export const IconButtonWithWhiteBackgroundAndShadow = styled(IconButton)({
  backgroundColor: Common.white.main,
  boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.08)",
  "&:hover": {
    backgroundColor: "#f0f0f0",
  },
});

const CustomBadge = styled(Badge)({
  left: "50%",
  transform: "translateX(-50%)",
  position: Positioning.absolute,
  bottom: "120%",
  maxWidth: "fit-content",
});

export const CHATBOT_SCROLL_TO_BOTTOM_BUTTON_ID = "chatbot-scroll-bottom-button";

function ChatbotScrollToBottomButton({ onClick, ...rest }: Readonly<IconButtonProps>) {
  const [hasNewMessage, setHasNewMessage] = useState<boolean>(false);
  const { state } = useContext(ChatbotContext);

  useEffect(() => {
    if (!state.isSending) {
      return;
    }

    const isLastMessageFromAi =
      state.messages.length > 0 && state.messages[state.messages.length - 1].senderType === SenderTypeEnum.ASSISTANT;
    if (isLastMessageFromAi && !hasNewMessage) {
      setHasNewMessage(true);
    }
  }, [hasNewMessage, state.isSending, state.messages]);

  const onClickHandler = useCallback(
    function onClickHandler(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
      setHasNewMessage(false);
      if (isFunction(onClick)) {
        onClick(event);
      }
    },
    [onClick]
  );

  return (
    <CustomBadge
      id={CHATBOT_SCROLL_TO_BOTTOM_BUTTON_ID}
      color="error"
      badgeContent={"1"}
      invisible={!hasNewMessage}
      overlap="circular"
    >
      <IconButtonWithWhiteBackgroundAndShadow size="medium" color="default" onClick={onClickHandler} {...rest}>
        <ArrowDownOutlined />
      </IconButtonWithWhiteBackgroundAndShadow>
    </CustomBadge>
  );
}

export default memo(ChatbotScrollToBottomButton);
